<template>
  <div class="p-3">
    <!-- Period selector -->
    <div class="flex items-center mb-3">
      <wz-select
        v-model="filters.month"
        :items="monthOptions"
        class="mr-2"
        style="min-width: 140px"
        @input="loadData"
      />
      <wz-select
        v-model="filters.year"
        :items="yearOptions"
        class="mr-2"
        style="min-width: 100px"
        @input="loadData"
      />
      <wz-button variant="light" size="sm" class="mr-2" @click="loadData">
        <i class="ri-refresh-line"></i>
      </wz-button>
      <wz-button size="sm" @click="$router.push({ name: 'payroll-movement-create', query: { employeeId: employeeId } })">
        <i class="ri-add-line mr-1"></i>Registrar movimiento
      </wz-button>
    </div>

    <!-- Summary cards -->
    <div class="flex flex-wrap mb-3 -mx-2" v-if="list.length > 0">
      <div class="w-full md:w-1/3 px-2">
        <div class="p-3 border rounded text-center">
          <div class="text-muted text-sm mb-1">Total descuentos</div>
          <div class="font-bold text-danger">{{ formatAmount(totalDiscounts) }}</div>
        </div>
      </div>
      <div class="w-full md:w-1/3 px-2">
        <div class="p-3 border rounded text-center">
          <div class="text-muted text-sm mb-1">Total adicionales</div>
          <div class="font-bold text-success">{{ formatAmount(totalAdditions) }}</div>
        </div>
      </div>
      <div class="w-full md:w-1/3 px-2">
        <div class="p-3 border rounded text-center">
          <div class="text-muted text-sm mb-1">Neto</div>
          <div class="font-bold" :class="netAmount >= 0 ? 'text-primary' : 'text-danger'">
            {{ formatAmount(netAmount) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <table class="wz-table w-full px-2">
      <thead>
        <tr>
          <th>Concepto</th>
          <th>Tipo</th>
          <th class="text-right">Monto</th>
          <th>Origen</th>
          <th>Estado</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="list.length === 0">
          <td colspan="6" class="text-center text-muted py-4">No hay movimientos para el período seleccionado.</td>
        </tr>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.conceptName }}</td>
          <td>
            <wz-badge :variant="getTypeColor(item.conceptType)">{{ getTypeLabel(item.conceptType) }}</wz-badge>
          </td>
          <td class="text-right">{{ formatAmount(item.amount) }}</td>
          <td>{{ getOriginLabel(item.origin) }}</td>
          <td>
            <wz-badge :variant="getStatusColor(item.status)">{{ item.status }}</wz-badge>
          </td>
          <td>{{ item.description || '-' }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Cuotas en curso -->
    <div class="mt-4" v-if="installmentGroups.length > 0">
      <h6 class="text-muted mb-3">
        <i class="ri-coins-line mr-1"></i>Cuotas en curso
      </h6>
      <table class="wz-table w-full px-2">
        <thead>
          <tr>
            <th>Concepto</th>
            <th class="text-right">Monto total</th>
            <th class="text-center">Cuotas</th>
            <th class="text-right">Saldo</th>
            <th>Estado</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="g in installmentGroups" :key="g.groupId">
            <td>{{ g.conceptName }}</td>
            <td class="text-right">{{ formatAmount(g.totalAmount) }}</td>
            <td class="text-center">{{ g.paidCount }}/{{ g.totalInstallments }}</td>
            <td class="text-right">{{ formatAmount(g.pendingBalance) }}</td>
            <td>
              <wz-badge :variant="getGroupStatusColor(g.status)">{{ getGroupStatusLabel(g.status) }}</wz-badge>
            </td>
            <td>
              <wz-button
                variant="light"
                size="sm"
                @click="$router.push({ name: 'movement-group-detail', params: { groupId: g.groupId } })"
              >Ver</wz-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { PayrollMovementService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'PayrollMovementsTab',
  
  props: {
    employeeId: { type: Number, default: null },
  },

  data() {
    const now = new Date()
    return {
      list: [],
      installmentGroups: [],
      monthOptions: MONTH_NAMES.map((m, i) => ({ key: i + 1, label: m })),
      filters: {
        month: now.getMonth() + 1,
        year:  now.getFullYear(),
      },
    }
  },

  computed: {
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({ key: current - 2 + i, label: String(current - 2 + i) }))
    },
    totalDiscounts() {
      return this.list
        .filter(m => m.conceptType === 'DESCUENTO' && m.status !== 'CANCELADO')
        .reduce((sum, m) => sum + Number(m.amount || 0), 0)
    },
    totalAdditions() {
      return this.list
        .filter(m => m.conceptType === 'ADICIONAL' && m.status !== 'CANCELADO')
        .reduce((sum, m) => sum + Number(m.amount || 0), 0)
    },
    netAmount() {
      return this.totalAdditions - this.totalDiscounts
    },
  },

  methods: {
    async loadData() {
      if (!this.employeeId) return
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollMovementService.getByPeriod({
          periodYear:  this.filters.year,
          periodMonth: this.filters.month,
        })
        const all = resp.payload || []
        this.list = all.filter(m => m.employeeId === this.employeeId)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los movimientos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    getTypeLabel(type) {
      const t = LocalConstants.PayrollConceptType[type]
      return t ? t.label : type
    },
    getTypeColor(type) {
      return type === 'DESCUENTO' ? 'danger-solid' : 'success-solid'
    },
    getOriginLabel(origin) {
      const o = LocalConstants.PayrollMovementOrigin[origin]
      return o ? o.label : origin
    },
    getStatusColor(status) {
      const s = LocalConstants.PayrollMovementStatus[status]
      return s ? s.color : 'dark'
    },
    formatAmount(value) {
      if (value == null) return 'S/ 0.00'
      return 'S/ ' + Number(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    getGroupStatusLabel(status) {
      const s = LocalConstants.InstallmentGroupStatus[status]
      return s ? s.label : status
    },
    getGroupStatusColor(status) {
      const s = LocalConstants.InstallmentGroupStatus[status]
      return s ? s.color : 'dark'
    },
    async loadInstallmentGroups() {
      if (!this.employeeId) return
      try {
        const resp = await PayrollMovementService.getInstallmentGroups({ employeeId: this.employeeId })
        this.installmentGroups = resp.payload || []
      } catch (e) {
        console.error('Error al cargar cuotas en curso', e)
      }
    },
  },

  created() {
    this.loadData()
    this.loadInstallmentGroups()
  },
}
</script>

<style lang="stylus" scoped></style>
